import { DeviceIdentificationType } from '@quadminds/shared/tracking-system';
import { isNull } from './is-null';
import { TripErrorCodeEnum } from '../enums';
import { ICheckpointOutputDto, IStepOutputDto, ITripOutputDto, TriggerOrder } from '../interfaces';

export class TripsHelperShared {
    static getCurrentCheckpointAndStepIndex(triggerOrder: TriggerOrder[]): {
        currentCheckpointIndex: number;
        currentStepIndex: number;
    } {
        if (!triggerOrder?.length) {
            return {
                currentCheckpointIndex: 0,
                currentStepIndex: 0,
            };
        }

        const currentCheckpointIndex = triggerOrder[triggerOrder.length - 1].checkpointIndex;
        const currentStepIndex = triggerOrder[triggerOrder.length - 1].stepIndex;

        return {
            currentCheckpointIndex,
            currentStepIndex,
        };
    }

    static getEntityNameByExternalIdSource(externalIdSource: string): 'vehicle' | 'driver' {
        const sources: { [key: string]: 'vehicle' | 'driver' } = {
            vehicles: 'vehicle',
            drivers: 'driver',
        };

        return sources[externalIdSource];
    }

    static getExternalIdSourceByDeviceIdentificationType(deviceIdentificationType: DeviceIdentificationType): string {
        const sources: { [key: string]: string } = {
            [DeviceIdentificationType.LICENSE_PLATE]: 'vehicles',
            [DeviceIdentificationType.PHONE]: 'drivers',
        };

        return sources[deviceIdentificationType];
    }

    static getError(
        error: TripErrorCodeEnum,
        metadata: Record<string, any>,
    ): { code: TripErrorCodeEnum; message: string; metadata: Record<string, any> } {
        const errors = {
            [TripErrorCodeEnum.DRIVER_DELETED]: 'Driver was deleted',
            [TripErrorCodeEnum.VEHICLE_DELETED]: 'Vehicle was deleted',
            [TripErrorCodeEnum.CARRIER_DELETED]: 'Carrier was deleted',
        };

        return {
            code: error,
            message: errors[error],
            metadata,
        };
    }

    static isFinishedTrip(trip: ITripOutputDto): boolean {
        return !isNull(trip.realFinish);
    }

    static isLastStepTriggered(checkpoints: ICheckpointOutputDto[]): boolean {
        const lastCp = checkpoints[checkpoints.length - 1];
        const lastStep = lastCp?.steps?.[lastCp?.steps?.length - 1];
        return !isNull(lastStep?.triggeredAt);
    }

    static getCheckpointInTransit(
        currentCheckpoint: number,
        currentStep: number,
        checkpoints: ICheckpointOutputDto[],
    ): { checkpoint: ICheckpointOutputDto | null; checkpointIndex: number } {
        const isCurrentLastStepOfCheckpoint = checkpoints[currentCheckpoint]?.steps.length - 1 === currentStep;
        let areAllStepsTriggered = true;
        for (const step of checkpoints[currentCheckpoint]?.steps ?? []) {
            if (isNull(step.triggeredAt)) {
                areAllStepsTriggered = false;
                break;
            }
        }
        if (!isCurrentLastStepOfCheckpoint && !areAllStepsTriggered) {
            return { checkpoint: null, checkpointIndex: -1 };
        }

        let firstPendingCheckpointIndex: number | null = null;
        for (const checkpoint of checkpoints.slice(1)) {
            if ((checkpoint.steps as IStepOutputDto[]).every((step) => isNull(step.triggeredAt))) {
                firstPendingCheckpointIndex = checkpoints.indexOf(checkpoint);
                break;
            }
        }

        const nextCheckpointIndex = this.isLastStepTriggered(checkpoints)
            ? firstPendingCheckpointIndex
            : currentCheckpoint + 1;

        if (nextCheckpointIndex === null) {
            return { checkpoint: null, checkpointIndex: -1 };
        }

        return {
            checkpoint: checkpoints[nextCheckpointIndex] || null,
            checkpointIndex: nextCheckpointIndex,
        };
    }

    static getStateDefinition(trip: ITripOutputDto): string | null {
        const { currentCheckpointIndex, currentStepIndex } = this.getCurrentCheckpointAndStepIndex(
            trip.triggerOrder as TriggerOrder[],
        );

        const currentStep = trip.checkpoints?.[currentCheckpointIndex]?.steps?.[currentStepIndex];

        const checkpointInTransit = this.getCheckpointInTransit(
            currentCheckpointIndex,
            currentStepIndex,
            trip.checkpoints,
        );

        if (checkpointInTransit.checkpoint) {
            return checkpointInTransit.checkpoint.transitionStateDefinition ?? null;
        }

        if (this.isLastStepTriggered(trip.checkpoints) && !this.isFinishedTrip(trip)) {
            return trip.finishStateDefinition ?? null;
        }

        return currentStep?.stateDefinition ?? null;
    }
}
